import { useEffect, useState } from 'react';

import type { RecorderProps } from '@karehero/llama/pwa/pages/ConversationalFormPage/ChatComposer';
import { joinLines } from '@karehero/helpers/string';

import { heardText } from 'utils/voiceTranscript';
import {
  MAX_RECORDING_DURATION_SECONDS,
  useAudioRecorder,
} from './useAudioRecorder';
import { useVoiceTranscript } from './useVoiceTranscript';

/**
 * Takes a finished voice note: its audio, length, levels, transcript if one is
 * worth reading, and where the API keeps its recording if it holds all of it.
 */
export type VoiceNoteDelivery = (
  file: File,
  durationSeconds: number,
  levels: readonly number[],
  transcript: string | null,
  recordingId: string | null,
) => void;

/**
 * useVoiceNoteDraft records a voice note and captions it as it is spoken, so
 * its words count before it is sent, and sends it with its transcript.
 */
export const useVoiceNoteDraft = (careCircleId?: string) => {
  const recorder = useAudioRecorder();
  const voiceTranscript = useVoiceTranscript({
    careCircleId,
    stream: recorder.stream,
  });
  // Their correction of the captions; they vouch for it, so it is read as it is.
  const [editedTranscript, setEditedTranscript] = useState<string | null>(null);
  // A note's words stay live while its last words settle, so its ticks hold until it is sent.
  const [sendingText, setSendingText] = useState('');

  const isUnsent =
    recorder.state === 'recording' ||
    (recorder.state === 'stopped' && !!recorder.file);
  const spokenText =
    isUnsent && voiceTranscript.caption
      ? (editedTranscript ?? heardText(voiceTranscript.caption))
      : '';
  /** The words recorded and not yet sent, including those still settling. */
  const liveText = joinLines([sendingText, spokenText]);

  useEffect(() => {
    if (
      recorder.state === 'recording' &&
      recorder.durationSeconds >= MAX_RECORDING_DURATION_SECONDS
    ) {
      recorder.stop();
    }
  }, [recorder]);

  const start = () => {
    setEditedTranscript(null);
    recorder.start();
  };

  const remove = () => {
    setEditedTranscript(null);
    recorder.reset();
  };

  // Cleared at once; sent once its last words settle, which is usually already.
  const send = async (deliver: VoiceNoteDelivery) => {
    const { file, durationSeconds, levels } = recorder;
    if (!file) return;
    const edited = editedTranscript;
    // Taken now: the session is ready long before the recording stops, and a
    // recording started while the words settle has an ID of its own.
    const { recordingId } = voiceTranscript;
    setSendingText(spokenText);
    recorder.reset();
    setEditedTranscript(null);
    const transcript =
      edited ?? (await voiceTranscript.takeTranscript(durationSeconds));
    deliver(file, durationSeconds, levels, transcript, recordingId);
    setSendingText('');
  };

  const recorderProps: Omit<RecorderProps, 'onSend'> = {
    state: recorder.state,
    durationSeconds: recorder.durationSeconds,
    levels: recorder.levels,
    maxDurationSeconds: MAX_RECORDING_DURATION_SECONDS,
    error: recorder.error ?? voiceTranscript.error,
    file: recorder.file,
    isPlaying: recorder.isPlaying,
    liveTranscript: voiceTranscript.caption,
    isCaptionShown: voiceTranscript.isShown,
    onToggleCaption: voiceTranscript.toggleShown,
    editedTranscript,
    onEditTranscript: setEditedTranscript,
    onStart: start,
    onStop: recorder.stop,
    onTogglePlay: recorder.togglePlay,
    onDelete: remove,
  };

  return { liveText, recorderProps, send };
};
